import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CsvImportService, ImportStatus } from '../../../services/csv-import.service';

@Component({
  selector: 'app-csv-import',
  templateUrl: './csv-import.component.html',
  styleUrls: ['./csv-import.component.scss']
})
export class CsvImportComponent implements OnInit, OnDestroy {
  @Input() storeId!: string;

  private destroy$ = new Subject<void>();

  // File upload state
  selectedFile: File | null = null;
  isDragOver = false;
  isUploading = false;

  // Import status
  importStatus: ImportStatus | null = null;
  isConnected = false;
  showResults = false;

  // UI helpers
  progressPercentage = 0;
  estimatedTimeRemaining = '';

  csvColumns = [
    'Category ID', 'Category', 'Category Visible', 'Product name', 'price', 'Orginal price',
    'Tax rate', 'SKU', 'Barcode', 'Description', 'Stock', 'Threshold', 'Track Inventory',
    'Visible For customers', 'Age Restricted', 'Age', 'Deposit fee', 'Cost price',
    'Provider deposit code', 'Image URL',
    'Main Group', 'Product Group', 'Sub Group', 'DSK Assortment ID', 'Family', 'Discount Group',
    'Unit Code', 'Unit Content'
  ];

  constructor(private csvImportService: CsvImportService) {}

  ngOnInit(): void {
    if (!this.storeId) {
      console.error('Store ID is required for CSV import component');
      return;
    }

    // Connect to socket for real-time updates
    this.csvImportService.connectSocket(this.storeId);

    // Subscribe to connection status
    this.csvImportService.connectionStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(connected => {
        this.isConnected = connected;
      });

    // Subscribe to import status updates
    this.csvImportService.importStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.importStatus = status;
        this.updateProgress(status);

        if (status.status === 'completed' || status.status === 'error') {
          this.isUploading = false;
          this.showResults = true;
          // Service clears activeImportId internally; keep UI in sync
          this.csvImportService.setActiveImportId(null);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.csvImportService.disconnectSocket();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.handleFileSelection(file);
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  private handleFileSelection(file: File): void {
    const validation = this.csvImportService.validateCSVFile(file);
    
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    this.selectedFile = file;
    this.resetImportState();
  }

  uploadFile(): void {
    if (!this.selectedFile || !this.storeId) {
      return;
    }

    this.isUploading = true;
    this.showResults = false;
    this.resetImportState();

    this.csvImportService.uploadCSVFile(this.storeId, this.selectedFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Upload successful:', response);
          // Register the importId so the service can recover status on reconnect
          const importId = (response as any)?.data?.importId || (response as any)?.response?.data?.importId;
          if (importId) {
            this.csvImportService.setActiveImportId(importId);
          }
        },
        error: (error) => {
          console.error('Upload failed:', error);
          this.isUploading = false;
          alert(error.error?.message || 'Upload failed. Please try again.');
        }
      });
  }

  private updateProgress(status: ImportStatus): void {
    if (status.totalRows > 0) {
      this.progressPercentage = Math.round((status.processedRows / status.totalRows) * 100);
    }

    // Estimate time remaining based on processing speed
    if (status.status === 'processing' && status.processedRows > 0) {
      const elapsed = new Date().getTime() - new Date(status.startTime).getTime();
      const avgTimePerRow = elapsed / status.processedRows;
      const remainingRows = status.totalRows - status.processedRows;
      const remainingMs = remainingRows * avgTimePerRow;
      
      if (remainingMs > 0) {
        const remainingSeconds = Math.round(remainingMs / 1000);
        if (remainingSeconds < 60) {
          this.estimatedTimeRemaining = `${remainingSeconds}s remaining`;
        } else {
          const minutes = Math.floor(remainingSeconds / 60);
          const seconds = remainingSeconds % 60;
          this.estimatedTimeRemaining = `${minutes}m ${seconds}s remaining`;
        }
      }
    }
  }

  private resetImportState(): void {
    this.importStatus = null;
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = '';
    this.showResults = false;
  }

  clearFile(): void {
    this.selectedFile = null;
    this.resetImportState();
  }

  downloadTemplate(): void {
    if (!this.storeId) {
      return;
    }
    this.csvImportService.downloadTemplate(this.storeId);
  }

  getFileSize(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  retry(): void {
    if (this.selectedFile) {
      this.uploadFile();
    }
  }
}
